import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Stamp, MapPin, Utensils, ChevronRight, Share2, Loader2, Plus } from "lucide-react";
import BrandCard from "@/components/shared/BrandCard";

export default function TruckPassport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brandCardOpen, setBrandCardOpen] = useState(false);
  const [brandCardTarget, setBrandCardTarget] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/passport/my-passport`),
          axios.get(`${API}/passport/stats`),
        ]);
        setPassport(pRes.data);
        setStats(sRes.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleShare = () => {
    const text = `My Food Truck Passport: ${stats?.total_trucks || 0} trucks visited across ${stats?.cities_covered || 0} cities! Favorite cuisine: ${stats?.favorite_cuisine || "exploring"}`;
    if (navigator.share) {
      navigator.share({ title: "My Truck Passport", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Passport stats copied!");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 text-[#F59E0B] animate-spin" />
      </div>
    );
  }

  const stamps = passport?.stamps || [];

  return (
    <div className="space-y-4" data-testid="truck-passport">
      {/* Header Stats */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#F59E0B] flex items-center justify-center">
              <Stamp className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">Truck Passport</p>
              <p className="text-[10px] text-slate-500">Your food truck journey</p>
            </div>
          </div>
          <button
            onClick={handleShare}
            className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center"
            data-testid="share-passport-btn"
          >
            <Share2 className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3" data-testid="passport-stats">
          <div className="text-center">
            <p className="text-xl font-black text-[#F59E0B]">{stats?.total_trucks || 0}</p>
            <p className="text-[9px] text-slate-500 font-medium">Trucks Visited</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-black text-[#F59E0B]">{stats?.cities_covered || 0}</p>
            <p className="text-[9px] text-slate-500 font-medium">Cities</p>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-[#F59E0B] truncate">{stats?.favorite_cuisine || "—"}</p>
            <p className="text-[9px] text-slate-500 font-medium">Fav Cuisine</p>
          </div>
        </div>
      </div>

      {/* Stamp Grid */}
      <div className="grid grid-cols-3 gap-2" data-testid="passport-stamp-grid">
        {stamps.map((stamp, i) => (
          <button
            key={stamp.business_id}
            onClick={() => navigate(`/truck/${stamp.business_id}`)}
            className="bg-white rounded-xl border border-slate-100 p-2.5 text-left hover:border-amber-200 transition group"
            data-testid={`passport-stamp-${i}`}
          >
            <div className="w-full aspect-square rounded-lg bg-slate-100 mb-2 overflow-hidden"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setBrandCardTarget({ id: stamp.business_id, name: stamp.name, logo: stamp.photo_url }); setBrandCardOpen(true); }}
              data-testid={`brand-tap-passport-${i}`}
            >
              {stamp.photo_url ? (
                <img src={stamp.photo_url} alt={stamp.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-amber-50">
                  <Utensils className="w-6 h-6 text-amber-300" />
                </div>
              )}
            </div>
            <p className="text-[10px] font-bold text-slate-800 truncate">{stamp.name}</p>
            <p className="text-[8px] text-slate-400">
              {stamp.cuisine_emoji} {stamp.total_visits} visit{stamp.total_visits !== 1 ? "s" : ""}
            </p>
            <p className="text-[8px] text-slate-300">{new Date(stamp.first_visit).toLocaleDateString()}</p>
          </button>
        ))}

        {/* Empty slots */}
        {Array.from({ length: Math.max(0, 6 - stamps.length) }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 p-2.5 flex flex-col items-center justify-center aspect-square"
            data-testid={`empty-stamp-${i}`}
          >
            <Plus className="w-5 h-5 text-slate-300 mb-1" />
            <p className="text-[8px] text-slate-300 font-medium text-center">Visit a truck to earn a stamp</p>
          </div>
        ))}
      </div>

      {stamps.length === 0 && (
        <div className="text-center py-6">
          <Stamp className="w-10 h-10 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400 font-medium">No stamps yet</p>
          <p className="text-[10px] text-slate-300">Visit food trucks to start your passport</p>
        </div>
      )}

      <BrandCard
        isOpen={brandCardOpen}
        onClose={() => setBrandCardOpen(false)}
        businessId={brandCardTarget?.id}
        businessName={brandCardTarget?.name}
        logoUrl={brandCardTarget?.logo}
      />
    </div>
  );
}
